import { useQuery } from '@tanstack/react-query';

import { useHelmRelease } from '@/react/kubernetes/helm/helmReleaseQueries/useHelmRelease';
import { getNamespaces } from '@/react/kubernetes/namespaces/queries/useNamespacesQuery';

import { useAddonEnvironmentId } from '../../useAddonEnvironmentId';

// Add-ons install into a deterministic, Portainer-owned namespace and use the
// add-on id as the Helm release name. This mirrors the backend convention
// (namespaceName in package/server-ee/api/addons/namespace.go, and release
// Name = id in service.go); the add-on API deliberately does not expose the
// namespace/release, so they are reconstructed here rather than plumbed through.
const ADDON_NAMESPACE_PREFIX = 'portainer-addon-';

export function addonNamespace(id: string) {
  return `${ADDON_NAMESPACE_PREFIX}${id}`;
}

/**
 * useAddonRelease fetches the add-on's Helm release, resolving the local
 * environment and namespace. The error toast is suppressed: the release can be
 * absent (removed outside Portainer or a failed install), and callers render an
 * inline error instead (see [[AddonReleaseGuard]]).
 */
export function useAddonRelease(
  id: string,
  {
    showResources = false,
    refetchInterval,
  }: { showResources?: boolean; refetchInterval?: number } = {}
) {
  const { environmentId, isLoading: isEnvLoading } = useAddonEnvironmentId({
    enabled: !!id,
  });
  const namespace = addonNamespace(id);

  const query = useHelmRelease(environmentId ?? 0, id, namespace, {
    showResources,
    refetchInterval,
    enabled: !!environmentId,
    withErrorNotification: false,
    // a missing release won't appear on retry, so fail fast
    retry: 1,
  });

  return { query, environmentId, namespace, isEnvLoading };
}

/**
 * useAddonNamespaceExists reports whether the add-on's namespace exists, gating
 * actions (e.g. restart) that only make sense once it's deployed. Keyed on the
 * environment (not the add-on), so a list of cards shares a single namespaces
 * request; each caller derives its own answer. No error toast — "missing" is an
 * expected outcome, not a failure to surface.
 */
export function useAddonNamespaceExists(id: string) {
  const { environmentId } = useAddonEnvironmentId({ enabled: !!id });
  const namespace = addonNamespace(id);

  return useQuery(
    ['addon-namespaces', environmentId],
    () => getNamespaces(environmentId ?? 0),
    {
      enabled: !!environmentId,
      select: (namespaces) => namespaces.some((ns) => ns.Name === namespace),
    }
  );
}
